<template>
    <!-- Card Row -->
    <div
        class="grid gap-4 text-sm border-slate-200 dark:border-slate-700 w-full border-b"
        :class="{
            'border-t': isAGroup && !index,
            'bg-slate-100 dark:bg-slate-900': card.ignore,
            'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 group': card.card_id,
            'pl-[48px]': isAGroup,
        }"
        :style="gridStyle"
        @click="toggleSidebar(card)"
    >
        <!-- Card Title -->
        <div
            class="flex gap-4 items-center sticky py-3 h-auto group-hover:bg-slate-50 dark:group-hover:bg-slate-700"
            :class="{
                'left-12 max-w-[272px]': isAGroup,
                'left-0 pl-6': !isAGroup,
                'border-r border-slate-200 dark:border-slate-700': isScrolledLeft,
                'bg-white dark:bg-slate-800': !card.ignore,
                'bg-slate-100 dark:bg-slate-900': card.ignore,
                'justify-self-start': isAGroup,
            }"
        >
            <div
                v-if="isAGroup"
                class="absolute bg-white dark:bg-slate-800 w-16 -left-16 -top-2 bottom-0 h-24"
            >
                <svg
                    v-if="index === 0"
                    width="12"
                    height="70"
                    viewBox="0 0 12 70"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="absolute -top-2 left-12"
                >
                    <path
                        d="M1.5 5V50Q1.5 63 12 63"
                        stroke="#CBD5E1"
                        stroke-width="1"
                        stroke-linecap="round"
                    />
                </svg>
                <svg
                    v-else
                    width="12"
                    height="110"
                    viewBox="0 0 12 110"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="absolute -top-12 left-12"
                >
                    <path
                        d="M1.5 0V85Q1.5 98 12 98"
                        stroke="#CBD5E1"
                        stroke-width="1"
                        stroke-linecap="round"
                    />
                </svg>
            </div>
            <div class="relative inline-block">
                <ImageLoader>
                    <template #image>
                        <img
                            :src="`${cdnUrl}/${card.card?.card_image?.path}/_small/${card.card?.card_image?.file_name}`"
                            class="max-w-[80px] h-auto"
                        />
                    </template>
                    <template #error>
                        <div
                            class="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900"
                        >
                            <div
                                class="w-20 h-12 bg-slate-100 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 flex items-center justify-center"
                            >
                                <WalletIcon
                                    icon="unmatched-card"
                                    class="h-6 w-6 text-slate-400 dark:text-slate-500"
                                />
                            </div>
                        </div>
                    </template>
                </ImageLoader>
                <!-- Badges overlay -->
                <div
                    class="absolute -bottom-2.5 -right-1 flex gap-0.5 flex-row"
                >
                    <!-- Hidden card badge -->
                    <Tooltip
                        v-if="card.ignore"
                        text="Card hidden in your wallet"
                        yPlacement="top"
                        xPlacement="right"
                        class="custom-tooltip custom-tooltip-left"
                    >
                        <template #trigger>
                            <div
                                class="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-white bg-slate-800 text-white shadow-lg"
                            >
                                <Icon icon="eye-on" class="w-2.5 h-2.5" />
                            </div>
                        </template>
                    </Tooltip>

                    <!-- Product change badge -->
                    <Tooltip
                        v-if="card.product_changed"
                        :text="getProductChangeTooltipText(card)"
                        yPlacement="top"
                        xPlacement="right"
                        class="custom-tooltip custom-tooltip-left"
                    >
                        <template #trigger>
                            <div
                                class="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-white bg-slate-800 text-white shadow-lg"
                            >
                                <Icon icon="swap" class="w-2.5 h-2.5" />
                            </div>
                        </template>
                    </Tooltip>

                    <!-- Multiple cards of same type badge -->
                    <Tooltip
                        v-if="hasMultipleCardsOfSameType"
                        :text="getMultipleCardsTooltipText"
                        yPlacement="top"
                        xPlacement="right"
                        class="custom-tooltip custom-tooltip-left"
                    >
                        <template #trigger>
                            <div
                                class="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-white bg-slate-800 text-white shadow-lg"
                            >
                                <span
                                    class="text-[10px] leading-none font-medium"
                                >
                                    x{{ sameTypeCardsCount }}
                                </span>
                            </div>
                        </template>
                    </Tooltip>
                </div>
            </div>
            <div class="flex flex-col font-medium text-slate-800 dark:text-slate-100">
                <div class="flex items-center justify-start">
                    <div
                        v-if="card.card_id"
                        class="max-w-72 cursor-pointer relative hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                        <span
                            class="block relative overflow-hidden truncate"
                            :class="{
                                'max-w-[176px]': isAGroup,
                                'max-w-[200px]': !isAGroup,
                            }"
                            @mouseover="
                                showTitleTooltip = shouldTruncate(
                                    card.card_title
                                )
                            "
                            @mouseleave="showTitleTooltip = false"
                        >
                            {{ truncateString(card.card_title) }}
                        </span>
                        <div
                            v-show="showTitleTooltip"
                            class="absolute bottom-full left-0 mb-2 py-1 px-2 text-sm text-white bg-slate-800 dark:bg-slate-700 rounded-lg shadow-lg dark:shadow-slate-900/50 z-50 whitespace-nowrap"
                        >
                            {{ card.card_title }}
                        </div>
                    </div>
                    <div
                        class="max-w-72 text-slate-500 dark:text-slate-400"
                        v-else-if="card.card_match_request"
                    >
                        <span
                            class="block relative overflow-hidden truncate max-w-40"
                        >
                            {{ card.card_match_request.card_not_found_name }}
                        </span>
                    </div>
                    <div class="max-w-72 text-slate-500 dark:text-slate-400" v-else>
                        <span
                            class="block relative overflow-hidden truncate max-w-40 min-w-40"
                        >
                            Unknown card
                        </span>
                    </div>
                </div>
                <Transition name="fade">
                    <div
                        class="text-xs text-slate-500 dark:text-slate-400"
                        v-if="
                            card.last_four &&
                            !card.card_match_request &&
                            showLastFour
                        "
                    >
                        ({{ card.last_four }})
                    </div>
                </Transition>
                <div
                    v-if="
                        card?.guid &&
                        card?.member_guid &&
                        !card?.converted_to_manual
                    "
                >
                    <span
                        class="text-xs flex items-center gap-1"
                        :class="{
                            'text-slate-500': !connectionProblem,
                            'text-red-500': connectionProblem,
                        }"
                    >
                        <span class="relative">
                            <Icon
                                :icon="
                                    !connectionProblem
                                        ? 'bank-financial'
                                        : 'bank-error'
                                "
                                class="w-4 h-4"
                                :class="{
                                    'text-slate-500': !connectionProblem,
                                    'text-red-500': connectionProblem,
                                }"
                            />
                        </span>
                        Connected via bank{{
                            connectionProblem ? ': Error' : ''
                        }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Bank -->
        <div
            v-if="isColumnVisible('institution_name')"
            class="flex items-center gap-2 py-3 h-full"
            :style="{ order: getColumnOrder('institution_name') }"
        >
            <ImageLoader>
                <template #image>
                    <div
                        class="w-7 h-7 flex items-center justify-center overflow-hidden group-hover:bg-slate-50 dark:group-hover:bg-slate-700"
                        :class="{
                            'bg-white dark:bg-slate-800': !card.ignore,
                            'bg-slate-100 dark:bg-slate-900': card.ignore,
                        }"
                    >
                        <img
                            :src="card.institution.small_logo_url"
                            class="max-w-[80%] max-h-[80%] rounded-full"
                            :alt="card.institution.name || 'Institution logo'"
                        />
                    </div>
                </template>
                <template #error>
                    <div
                        class="w-7 h-7 bg-slate-100 dark:bg-slate-900 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400"
                    >
                        <Icon icon="bank" class="h-5 w-5" />
                    </div>
                </template>
            </ImageLoader>
            <span class="text-slate-800 dark:text-slate-100">
                <span
                    ref="bankTooltipTrigger"
                    class="block overflow-hidden truncate max-w-32"
                    @mouseover="
                        showBankTooltip = shouldTruncate(
                            card.institution.issuing_bank.name
                        )
                    "
                    @mouseleave="showBankTooltip = false"
                >
                    {{ truncateString(card.institution.issuing_bank.name) }}
                </span>
                <div
                    v-show="showBankTooltip"
                    ref="bankTooltipFloating"
                    :style="bankTooltipStyles"
                    class="py-1 px-2 text-sm text-white bg-slate-800 dark:bg-slate-700 rounded-lg shadow-lg dark:shadow-slate-900/50 z-30 whitespace-nowrap"
                >
                    {{ card.institution.issuing_bank.name }}
                </div>
            </span>
        </div>

        <div
            v-if="isColumnVisible('card_type')"
            class="py-3 flex items-center h-full"
            :style="{ order: getColumnOrder('card_type') }"
        >
            {{ !card.card_id ? '--' : $filters.titleCase(card.card?.type) }}
        </div>
        <div
            v-if="isColumnVisible('user_name')"
            class="flex flex-col py-3 justify-center h-full"
            :style="{ order: getColumnOrder('user_name') }"
        >
            <div class="flex items-center gap-2">
                <div
                    class="flex h-6 w-6 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-900"
                >
                    <Icon
                        :icon="card.is_business ? 'business' : 'user'"
                        class="w-4 h-4 text-slate-500 dark:text-slate-400"
                    />
                </div>
                <div class="flex flex-col items-start">
                    <div class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5">
                        {{ formatEntityName(card.user_name) }}
                    </div>
                    <div
                        v-show="card.account_type !== 'cardholder'"
                        class="text-slate-500 dark:text-slate-400 text-xs font-normal leading-4"
                    >
                        Authorized user
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="isColumnVisible('account_type')"
            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 whitespace-nowrap py-3 flex items-center h-full"
            :style="{ order: getColumnOrder('account_type') }"
        >
            {{ $filters.titleCase(card.account_type?.replace('_', ' ')) }}
        </div>
        <div
            v-if="isColumnVisible('credit_limit')"
            class="text-right py-3 flex items-center justify-end h-full"
            :style="{ order: getColumnOrder('credit_limit') }"
        >
            {{ formattedCreditLimit }}
        </div>
        <div
            v-if="isColumnVisible('card_status')"
            class="py-3 flex h-full items-center"
            :style="{ order: getColumnOrder('card_status') }"
        >
            <div v-if="!card.card_id" class="text-slate-500 dark:text-slate-400">--</div>
            <div
                v-else-if="!card.unmatched"
                class="flex items-center justify-end text-sm w-full"
            >
                <span
                    v-if="card.card_status === 'open'"
                    class="inline-flex items-center px-3 py-0.5 font-medium rounded-full bg-emerald-50 text-emerald-500 dark:bg-emerald-500/30 dark:text-emerald-200 capitalize"
                >
                    Open
                </span>
                <span
                    v-else-if="card.card_status === 'closed'"
                    class="inline-flex items-center rounded-full bg-red-100 px-3 py-0.5 font-medium text-red-700 dark:bg-red-500/30 dark:text-red-200"
                >
                    Closed
                </span>
                <span
                    v-else
                    class="inline-flex items-center px-3 py-0.5 font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 capitalize"
                >
                    {{ card.card_status || 'Unknown' }}
                </span>
            </div>
        </div>
        <div
            v-if="isColumnVisible('date_opened')"
            class="p-3 flex items-center h-full"
            :style="{ order: getColumnOrder('date_opened') }"
        >
            <Tooltip
                v-if="!card.card_id || !card.date_opened"
                text="Add the date you opened this card to use all of My10x's features"
            >
                <template #trigger>
                    <p>--</p>
                </template>
            </Tooltip>
            <p v-else class="whitespace-nowrap">
                {{ formatDate(card.date_opened) }}
            </p>
        </div>
        <div
            v-if="isColumnVisible('date_closed')"
            class="py-3 flex items-center h-full whitespace-nowrap"
            :style="{ order: getColumnOrder('date_closed') }"
        >
            {{ !card.card_id ? '--' : formatDate(card.date_closed) }}
        </div>
        <div
            v-if="isColumnVisible('annual_fee_computed')"
            class="text-right py-3 flex items-center justify-end h-full"
            :style="{ order: getColumnOrder('annual_fee_computed') }"
        >
            <span v-if="!card.card_id">--</span>
            <AnnualFee
                v-else
                :item="card"
                :showLabel="false"
                :alignment="'end'"
            />
        </div>
        <div
            v-if="isColumnVisible('welcome_bonus')"
            class="text-left py-3 flex items-center h-full"
            :style="{ order: getColumnOrder('welcome_bonus') }"
        >
            <span v-if="!card.card_id">--</span>
            <div
                v-else-if="card.welcome_bonus !== null"
                class="flex items-center gap-4 min-w-0"
            >
                <span
                    v-if="card.welcome_bonus.received"
                    class="flex flex-col gap-[2px] justify-center items-start shrink-0"
                >
                    <span
                        class="inline-flex items-center px-3 py-0.5 font-medium rounded-full bg-emerald-50 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-300 capitalize text-sm w-fit"
                    >
                        Received
                    </span>
                    <p
                        class="text-left text-slate-500 dark:text-slate-400 text-xs font-normal leading-none"
                        v-show="card.welcome_bonus.date_received"
                    >
                        {{ formatDate(card.welcome_bonus.date_received) }}
                    </p>
                </span>
                <!-- MY1-3041: tiered bonuses roll up into an "x/y received"
                     pill; due date and bar follow the active tier. -->
                <span
                    v-else-if="welcomeBonusTiersDisplay"
                    class="flex flex-col gap-[2px] justify-center items-start shrink-0"
                >
                    <span
                        class="inline-flex items-center gap-1 px-3 py-0.5 font-medium rounded-full text-sm w-fit whitespace-nowrap"
                        :class="
                            welcomeBonusTiersDisplay.receivedCount > 0
                                ? 'bg-emerald-50 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                        "
                    >
                        <svg
                            v-if="welcomeBonusTiersDisplay.partial"
                            viewBox="0 0 12 12"
                            class="w-3 h-3 shrink-0"
                            aria-hidden="true"
                        >
                            <circle
                                cx="6"
                                cy="6"
                                r="5"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                            />
                            <path d="M6 1a5 5 0 0 0 0 10Z" fill="currentColor" />
                        </svg>
                        {{ welcomeBonusTiersDisplay.label }}
                    </span>
                    <p
                        class="text-left text-slate-500 dark:text-slate-400 text-xs font-normal leading-none"
                        v-show="welcomeBonusDueDate"
                    >
                        Due:
                        {{ formatDate(welcomeBonusDueDate) }}
                    </p>
                </span>
                <span
                    v-else
                    class="flex flex-col gap-[2px] justify-center items-start shrink-0"
                >
                    <span
                        class="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-700 px-3 py-0.5 font-medium text-slate-500 dark:text-slate-400 text-sm w-fit whitespace-nowrap"
                    >
                        Not received
                    </span>
                    <p
                        class="text-left text-slate-500 dark:text-slate-400 text-xs font-normal leading-none"
                        v-show="card.welcome_bonus.minimum_spend_deadline"
                    >
                        Due:
                        {{
                            formatDate(
                                card.welcome_bonus.minimum_spend_deadline
                            )
                        }}
                    </p>
                </span>
                <BonusProgressBar
                    v-if="welcomeBonusTrackingRow"
                    :bonus="welcomeBonusTrackingRow"
                    class="w-[120px] shrink-0"
                />
            </div>
        </div>
        <div
            v-if="isColumnVisible('bonus_offers')"
            class="text-right py-3 flex items-center justify-end h-full group-hover:bg-slate-50 dark:group-hover:bg-slate-700"
            :style="{ order: getColumnOrder('bonus_offers') }"
        >
            <span v-if="!card.card_id">--</span>
            <span
                v-else-if="
                    bonusOffersDisplay && !bonusOffersDisplay.allReceived
                "
                class="pill !mr-1 !bg-slate-100 dark:!bg-slate-700 !text-slate-500 dark:!text-slate-400 !px-3 !py-0.5 !font-medium !text-xs !lg:text-sm leading-none xl:ml-0 -translate-y-[4px] xl:translate-y-0 !border-none"
                @click="toggleOffersDropdown(card)"
            >
                {{ bonusOffersDisplay?.unreceived }}
            </span>
            <span
                v-else-if="bonusOffersDisplay"
                class="pill !mr-1 !bg-emerald-50 !text-emerald-500 !px-3 !py-0.5 !font-medium !text-xs !lg:text-sm leading-none xl:ml-0 -translate-y-[4px] xl:translate-y-0 !border-none"
            >
                {{ bonusOffersDisplay?.ratio }}
            </span>
        </div>
        <div
            v-if="isColumnVisible('chase_524')"
            class="text-right py-3 flex items-center justify-end h-full group-hover:bg-slate-50 dark:group-hover:bg-slate-700"
            :style="{ order: getColumnOrder('chase_524') }"
        >
            <span v-if="!card.card_id">--</span>
            <Tooltip
                v-else-if="card.contributes_to_wallet_five_twenty_four"
                :text="`Your ${card.card_title} will fall out of your 5/24 count on approximately ${card.five_twenty_four_fall_off_date}.`"
            >
                <template #trigger>
                    <span
                        class="pill !mr-1 !bg-slate-100 dark:!bg-slate-700 !text-slate-500 dark:!text-slate-400 !px-3 !py-0.5 !font-medium !text-xs !lg:text-sm leading-none xl:ml-0 -translate-y-[4px] xl:translate-y-0 cursor-help !border-none"
                    >
                        {{ card.five_twenty_four_level }}/24
                    </span>
                </template>
            </Tooltip>
        </div>
        <div
            v-if="isColumnVisible('actions')"
            class="sticky right-0 h-full flex items-center justify-center w-full border-l p-3 self-stretch z-20 group-hover:bg-slate-50 dark:group-hover:bg-slate-700"
            :class="{
                cursorPointer: card.ignore,
                'bg-white dark:bg-slate-800': !card.ignore,
                'bg-slate-100 dark:bg-slate-900': card.ignore,
            }"
            :style="{ order: getColumnOrder('actions') }"
            @click.stop
        >
            <button
                type="button"
                class="rounded-full w-8 lg:w-10 h-8 lg:h-10 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 outline-none"
                @click="toggleCardDropdown(card, $event)"
                ref="cardDropdownButton"
            >
                <Icon icon="dots-vertical" class="h-5 w-5" />
            </button>
        </div>
        <div
            v-if="card.card_id"
            class="sticky right-0 h-full flex items-center justify-center w-full border-l px-3 py-3 self-stretch z-10 group-hover:bg-slate-50 dark:group-hover:bg-slate-700"
            :class="{
                'border-slate-200 dark:border-slate-700': isScrolledRight,
                'border-transparent': !isScrolledRight,
                'bg-white dark:bg-slate-800': !card.ignore,
                'bg-slate-100 dark:bg-slate-900': card.ignore,
            }"
            :style="{ order: DEFAULT_COLUMN_ORDER }"
            @click.stop
        >
            <button
                type="button"
                class="rounded-full w-8 lg:w-10 h-8 lg:h-10 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 outline-none"
                @click="toggleCardDropdown(card, $event)"
                ref="cardDropdownButton"
            >
                <Icon icon="dots-vertical" class="h-5 w-5" />
            </button>
        </div>
        <div
            v-else
            class="sticky right-0 h-full flex items-center justify-end px-2 py-3 self-stretch z-20 w-[60px] overflow-visible group-hover:bg-slate-50 dark:group-hover:bg-slate-700"
            :class="{
                'bg-white dark:bg-slate-800': !card.ignore,
                'bg-slate-100 dark:bg-slate-900': card.ignore,
            }"
            :style="{ order: DEFAULT_COLUMN_ORDER }"
            @click.stop
        >
            <span class="relative">
                <button
                    @click="openCardMatchModal(card)"
                    class="btn-base btn-white whitespace-nowrap -translate-x-1 transform min-w-fit"
                >
                    Match
                </button>
                <!-- Card match request rejected notification badge -->
                <div class="absolute -bottom-2.5 -left-1 flex gap-0.5 flex-row">
                    <Tooltip
                        v-if="
                            card.card_match_request &&
                            card.card_match_request.rejected
                        "
                        text="Card match request rejected"
                        yPlacement="top"
                        xPlacement="left"
                        class="custom-tooltip custom-tooltip-left"
                    >
                        <template #trigger>
                            <div
                                class="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-white bg-red-500 text-white shadow-lg"
                            >
                                <Icon icon="alert" class="w-2.5 h-2.5" />
                            </div>
                        </template>
                    </Tooltip>
                </div>
            </span>
        </div>
    </div>
    <Transition name="fade-dropdown">
        <div
            class="w-full max-w-44 p-2 z-50 mb-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700"
            ref="carddropdownfloating"
            v-if="showCardDropdown"
            :style="{
                ...floatingStyles,
            }"
        >
            <ul>
                <li class="flex flex-start">
                    <a
                        class="btn-base btn-transparent !w-full !justify-start !px-2 !py-1.5 !mt-0"
                        href="#"
                        @click.prevent="
                            showCardDropdown = false;
                            openEditCardModal();
                        "
                    >
                        Edit
                    </a>
                </li>
                <li
                    class="flex flex-start"
                    v-if="
                        !activeAccountDropdown.guid &&
                        !activeAccountDropdown.member_guid
                    "
                >
                    <a
                        class="btn-base btn-transparent !w-full !justify-start !px-2 !py-1.5 !mt-0"
                        href="#"
                        role="button"
                        @click.prevent="
                            showCardDropdown = false;
                            openCopyCardModal({ account: card, user });
                        "
                    >
                        Copy card details
                    </a>
                </li>
                <li
                    class="flex flex-start"
                    v-if="
                        !activeAccountDropdown.guid &&
                        !activeAccountDropdown.member_guid
                    "
                >
                    <a
                        class="btn-base btn-transparent !w-full !justify-start !px-2 !py-1.5 !mt-0"
                        href="#"
                        role="button"
                        @click.prevent="
                            showCardDropdown = false;
                            openDeleteModal();
                        "
                    >
                        Remove
                    </a>
                </li>
            </ul>
        </div>
    </Transition>
    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
        <ConfirmationDialog
            :show="showDeleteConfirmation"
            :title="deleteDialogTitle"
            :dialog="deleteDialogText"
            icon="trash"
            iconColor="red"
            @cancel="handleDeleteCancel"
            @confirm="handleDeleteConfirm"
            :buttons="[
                { type: 'cancel', text: 'Cancel' },
                {
                    type: 'confirm',
                    text: 'Delete card',
                    value: true,
                    classes: '!bg-red-500 text-white',
                },
            ]"
        />
    </Teleport>
</template>
<script setup>
import { computed, ref, inject, Teleport } from 'vue';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';
import dayjs from 'dayjs';
import Filters from '@/filters/index.js';
import AnnualFee from '@/components/wallet/annual-fee.vue';
import BonusProgressBar from '@/components/wallet/partials/bonus-progress-bar.vue';
import { useWelcomeBonusRollup } from '@/composables/useBonusTracking';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';
import WalletIcon from '@/components/wallet/wallet-icon.vue';
import Tooltip from '@/components/tooltip.vue';
import { formatEntityName } from '@/utils/entityNameFormatter.js';

import {
    useFloating,
    computePosition,
    offset,
    flip,
    shift,
    autoUpdate,
} from '@floating-ui/vue';

import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';

const props = defineProps({
    card: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        default: 0,
    },
    isAGroup: {
        type: Boolean,
        default: false,
    },
    gridStyle: {
        type: Object,
        required: true,
    },
    totalCards: {
        type: Number,
        default: 0,
    },
});

const emit = defineEmits(['edit', 'delete', 'view-details', 'copy-card']);
const cdnUrl = ref(window.cdnUrl);
const reference = ref(null);
const floating = ref(null);
const cardDropdownButton = ref(null);
const carddropdownfloating = ref(null);
const bankTooltipTrigger = ref(null);
const bankTooltipFloating = ref(null);
const deleteActionVisible = ref(false);
//const { floatingStyles } = useFloating(floating, reference);

const walletStore = useWalletStore();

const isScrolledLeft = inject('isScrolledLeft', ref(false));
const isScrolledRight = inject('isScrolledRight', ref(false));

const { floatingStyles } = useFloating(
    cardDropdownButton,
    carddropdownfloating,
    {
        placement: 'bottom-start',
        middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    }
);

const { floatingStyles: bankTooltipStyles } = useFloating(
    bankTooltipTrigger,
    bankTooltipFloating,
    {
        placement: 'top',
        middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    }
);

const {
    showLastFour,
    visibleColumns,
    walletCards,
    columnOrder,
    activeAccountDropdown,
    groupSameCards,
    selectedCard,
} = storeToRefs(walletStore);
const {
    setShowEditCardModal,
    setSelectedCard,
    deleteCard,
    setAccountSidebar,
    openCardMatchModal,
    getConnectionProblemForCard,
    setShowCopyCardModal,
} = walletStore;

const showCardDropdown = ref(false);

// Constants
const DEFAULT_COLUMN_ORDER = 999; // Order for actions and unknown columns

const toggleCardDropdown = (card, el) => {
    showCardDropdown.value = !showCardDropdown.value;
};

const showDeleteConfirmation = ref(false);

const cardDisplayName = computed(() => {
    return (
        props.card.card_title ||
        props.card.card_match_request?.card_not_found_name ||
        'Card'
    );
});

const deleteDialogTitle = computed(() => `Delete "${cardDisplayName.value}"`);
const deleteDialogText = computed(
    () =>
        `You are about to delete your ${cardDisplayName.value}. This also deletes its benefit usage history, including credits logged on it, and its benefit settings. This action cannot be undone.`
);

const openDeleteModal = () => {
    showDeleteConfirmation.value = true;
};

const handleDeleteCancel = () => {
    showDeleteConfirmation.value = false;
};

const handleDeleteConfirm = async () => {
    // Ensure we have a valid card before attempting to delete
    if (!props.card || !props.card.id) {
        console.error('handleDeleteConfirm: Invalid card data', props.card);
        showDeleteConfirmation.value = false;
        return;
    }

    await deleteCard(props.card);
    showDeleteConfirmation.value = false;
};

const getColumnOrder = (columnId) => {
    const index = columnOrder.value.indexOf(columnId);
    return index >= 0 ? index : DEFAULT_COLUMN_ORDER; // Put unknown columns at the end
};

// MY1-3041: tiered welcome bonuses (MY1-3039) roll the pill up into
// "x/y received"; the due date and progress bar follow the active tier.
const {
    tiersDisplay: welcomeBonusTiersDisplay,
    trackingRow: welcomeBonusTrackingRow,
    dueDate: welcomeBonusDueDate,
} = useWelcomeBonusRollup(() => props.card.welcome_bonus);

const bonusOffersDisplay = computed(() => {
    if (!props.card.bonus_offers?.length) return null;
    let totalOffers = props.card.bonus_offers?.length;
    let offersReceived = props.card.bonus_offers.filter(
        (offer) => offer.received
    ).length;
    let ratio = `${offersReceived}/${totalOffers}`;
    let allReceived = offersReceived === totalOffers;
    return { ratio, allReceived };
});

const connectionProblem = computed(() => {
    return getConnectionProblemForCard(props.card);
});

const showErrorTooltip = ref(false);

const getConnectionErrorTooltip = () => {
    const bankName = props.card.institution?.issuing_bank?.name || 'your bank';
    return `Problem with bank connection via ${bankName}. Check "Manage connections" for details.`;
};

const sameTypeCardsCount = computed(() => {
    if (!props.card.card_id || !walletCards.value) return 0;
    const sameTypeCards = walletCards.value.filter(
        (card) => card.card_id === props.card.card_id
    );
    return sameTypeCards.length;
});

const hasMultipleCardsOfSameType = computed(() => {
    return (
        groupSameCards.value && sameTypeCardsCount.value > 1 && !props.isAGroup
    );
});

const getMultipleCardsTooltipText = computed(() => {
    const count = sameTypeCardsCount.value;
    return `Group of ${count} identical cards`;
});

const formattedCreditLimit = computed(() => {
    if (
        !props.card.card_id ||
        props.card.credit_limit === null ||
        props.card.credit_limit === undefined
    ) {
        return '--';
    }

    // Handle zero values explicitly
    if (props.card.credit_limit === 0 || props.card.credit_limit === '0.00') {
        return '$0';
    }

    // Use formatThousand for non-zero values
    return `$${Filters.formatThousand(Math.trunc(props.card.credit_limit))}`;
});

const openEditCardModal = () => {
    setSelectedCard(props.card);
    setShowEditCardModal(true);
};

const openCopyCardModal = ({ account }) => {
    setSelectedCard(account);
    setShowCopyCardModal(true);
};

const isColumnVisible = (columnName) => {
    return visibleColumns.value.includes(columnName);
};

const showTitleTooltip = ref(false);
const showBankTooltip = ref(false);

const shouldTruncate = (s) => {
    return s && s.length > 20;
};

const truncateString = (s) => {
    if (!s) return '';
    if (s.length <= 30) return s;
    return s.substring(0, 30) + '...';
};

const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return Filters.formatMoney(value);
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM D, YYYY');
};

const toggleSidebar = (item) => {
    // Don't open sidebar for unmatched cards (cards without card_id)
    if (!item.card_id) {
        return;
    }

    const isCurrentlySelected = item === selectedCard.value;
    const shouldShow = !isCurrentlySelected;

    if (shouldShow) {
        setSelectedCard(item);
        setAccountSidebar(true);
    } else {
        setAccountSidebar(false);
        setTimeout(() => setSelectedCard(null), 300);
    }
};

const getProductChangeTooltipText = (card) => {
    const changeDate = card.product_changed_date
        ? dayjs(card.product_changed_date).format('MMM DD, YYYY')
        : 'an unknown date';

    const originalCardName =
        card.original_card_name || card.card?.card_title || 'another product';

    const words = originalCardName.split(' ');
    const firstPart = words.slice(0, -2).join(' ');
    const secondPart = words.slice(-2).join(' ');

    return `Your product changed from ${firstPart}\n${secondPart} on ${changeDate} and this\nis the original date that product was opened.`;
};
</script>

<style scoped>
.fade-dropdown-enter-active,
.fade-dropdown-leave-active {
    transition: opacity 0.2s ease;
}
.fade-dropdown-enter-from,
.fade-dropdown-leave-to {
    opacity: 0;
}

/* Custom tooltip styles */
.custom-tooltip {
    z-index: 10 !important;
}

:deep(.custom-tooltip .tooltip) {
    z-index: 10 !important;
    position: relative;
}

:deep(.custom-tooltip .tooltip > div) {
    z-index: 10 !important;
}

/* Override tooltip content styles */
:deep(.custom-tooltip .min-w-44) {
    display: inline-flex;
    padding: 4px 8px;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 8px;
    background: #1e293b;
}
</style>
